import { useEffect } from "react";
import type { JSX, ReactNode } from "react";
import { useAiModels } from "./hooks/useAiModels";
import { useAgentEngine } from "./hooks/useAgentEngine";
import { PageSectionHeader } from "../../components/ui";
import { GroupCard, AgentRow, SubStatus } from "./aiModelsShared";
import { readinessLabel } from "./helpers";

// Agent settings page: pick the default agent that answers chats. Claude Code /
// Codex are transitional — the plan is for Magnis Agent to be the only agent.
export function AgentPanel(): JSX.Element {
  const {
    models,
    defaults,
    providers,
    subscriptionStatus,
    subscriptionStatusError,
    loadSubscriptionStatus,
  } = useAiModels();
  const { engines, current, loading, readiness, setEngine, lastError } = useAgentEngine();

  // The Magnis Agent's default chat model — set on the Models page. Shown here
  // so "which model does the agent use" is visible where you pick the agent.
  const defaultChatModel = models.find(
    (m) => m.id === defaults.find((d) => d.capability === "chat")?.model_id,
  );

  // Codex's live dot needs the sidecar status; load it when Codex is present.
  const needsCodexStatus =
    engines.includes("codex") ||
    providers.some((p) => p.auth_kind === "codex_subscription");
  useEffect(() => {
    if (needsCodexStatus) void loadSubscriptionStatus();
  }, [needsCodexStatus, loadSubscriptionStatus]);

  return (
    <div className="px-6 py-5 space-y-4">
      <PageSectionHeader
        title="Agent"
        description="Choose the agent that answers your chats by default. You can override it per chat from the composer."
      />

      {loading && engines.length === 0 && (
        <div className="flex items-center justify-center py-10 text-content-tertiary text-sm">
          Loading...
        </div>
      )}

      {!loading && engines.length === 0 && (
        <div className="p-3 rounded-lg bg-surface-tertiary border border-edge text-xs text-content-tertiary">
          No agents available — the agent runtime is not reachable.
        </div>
      )}

      {lastError !== null && (
        <div className="p-3 rounded-lg bg-amber/10 border border-amber/30 text-xs text-amber">
          {lastError}
        </div>
      )}

      {engines.length > 0 && (
        <GroupCard>
          {engines.map((name) => {
            // The backend's merged verdict — machine probe plus its own model
            // state. An engine with no entry is not selectable: offering one
            // on the strength of the static list is what produced a chat that
            // failed on the first message.
            const verdict = readiness[name];
            const usable = verdict?.ready === true;
            let status: ReactNode = null;
            if (!usable) {
              status = (
                <span className="text-xs text-amber" title={verdict?.detail}>
                  {readinessLabel(verdict?.reason)}
                </span>
              );
            } else if (name === "builtin") {
              status = defaultChatModel ? (
                <span className="text-xs text-content-tertiary">{defaultChatModel.name}</span>
              ) : (
                <span className="text-xs text-content-tertiary">
                  {models.length} {models.length === 1 ? "model" : "models"}
                </span>
              );
            } else if (name === "codex") {
              status = (
                <SubStatus status={subscriptionStatus} statusError={subscriptionStatusError} />
              );
            }
            return (
              <div key={name} className={usable ? undefined : "opacity-60"}>
                <AgentRow
                  name={name}
                  active={name === current}
                  status={status}
                  // Selecting an unready engine would be refused by the
                  // backend anyway; not firing the call keeps the row honest
                  // instead of flashing a selection that snaps back.
                  onSelect={
                    usable
                      ? (): void => void setEngine(name)
                      : (): void => undefined
                  }
                />
                {!usable && verdict?.detail !== undefined && verdict.detail !== "" && (
                  <p className="px-4 pb-3 -mt-1 text-xs text-content-tertiary">{verdict.detail}</p>
                )}
              </div>
            );
          })}
        </GroupCard>
      )}
    </div>
  );
}
